import React, {useState, useEffect} from 'react';
import {View, TouchableOpacity, StyleSheet, TextInput, ScrollView} from 'react-native';
import SlideModal from '@/Atoms/SlideModal';
import CCText from '@/Atoms/CCText';
import CCButton from '@/Atoms/CCButton';
import {FontSizes, isPhone} from '@/Utils/Dimensions';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import CCColors, {updateColorWithOpacity} from '@/Utils/CCColors';
import useGetStudentClassFeedbackForm from '@/Hooks/WebinarApi/useGetStudentClassFeedbackForm';
import useSubmitStudentClassFeedback from '@/Hooks/WebinarApi/useSubmitStudentClassFeedback';
import Divider from '@/Atoms/Divider/Divider';

// Phase 1 : Rate both (stars)
// Phase 2 : Coach improvement chips + coach comment  [shown only if coachRating < 5]
// Phase 3 : CircleChess improvement chips + cc comment [shown only if ccRating < 5]

type Phase = 1 | 2 | 3;

type Props = {
  visible: boolean;
  onClose: () => void;
  topic: string;
  coachName: string;
  webinarId: number;
  classType?: string;
  userData;
  onSubmit: (payload: any) => void;
};

export default function ClassReviewModal({
  visible,
  onClose,
  topic,
  coachName,
  webinarId,
  classType,
  userData,
  onSubmit,
}: Props) {
  const [phase, setPhase] = useState<Phase>(1);
  const [coachRating, setCoachRating] = useState<number | null>(null);
  const [ccRating, setCcRating] = useState<number | null>(null);
  const [coachReasonIds, setCoachReasonIds] = useState<string[]>([]);
  const [ccReasonIds, setCcReasonIds] = useState<string[]>([]);
  const [coachComment, setCoachComment] = useState('');
  const [ccComment, setCcComment] = useState('');
  const [coachHover, setCoachHover] = useState<number | null>(null);
  const [ccHover, setCcHover] = useState<number | null>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const {data: feedbackForm} = useGetStudentClassFeedbackForm(
    classType,
    userData?.user_id,
    webinarId,
  );

  // Pre-fill from partial submission if the user has already started
  const existingFeedback = (feedbackForm as any)?.data?.existing_feedback;

  // Safely convert whatever reason_ids format comes back (array or comma-string)
  const parseIds = (val: any): string[] => {
    if (!val) return [];
    if (Array.isArray(val)) return val.map(String);
    return String(val).split(',').map(s => s.trim()).filter(Boolean);
  };

  useEffect(() => {
    if (existingFeedback) {
      setCoachRating(existingFeedback.rating ?? null);
      setCcRating(existingFeedback.cc_rating ?? null);
      setCoachReasonIds(parseIds(existingFeedback.reason_ids));
      setCcReasonIds(parseIds(existingFeedback.cc_reason_ids));
      setCoachComment(existingFeedback.comment || '');
      setCcComment(existingFeedback.cc_comment || '');
      setIsSubmitted(existingFeedback.is_submitted || false);
    } else {
      // Reset state when switching to a different class that has no feedback
      setPhase(1);
      setCoachRating(null);
      setCcRating(null);
      setCoachReasonIds([]);
      setCcReasonIds([]);
      setCoachComment('');
      setCcComment('');
      setIsSubmitted(false);
    }
  }, [existingFeedback, webinarId]);

  const improvementStep = (feedbackForm as any)?.data?.steps?.find(
    (step: any) => step.step === 'IMPROVEMENTS',
  );
  const coachReasons: any[] = improvementStep?.coach_reasons ?? [];
  const ccReasons: any[] = improvementStep?.cc_reasons ?? [];

  const submitFeedbackMutation = useSubmitStudentClassFeedback();

  // ─── Navigation helpers ───────────────────────────────────────────────────

  /** Which phases will be shown given current ratings? */
  const needsCoachScreen = !!coachRating && coachRating < 5;
  const needsCcScreen = !!ccRating && ccRating < 5;

  const nextPhaseAfter1: Phase = needsCoachScreen ? 2 : needsCcScreen ? 3 : 1;
  const nextPhaseAfter2: Phase = needsCcScreen ? 3 : 1; // 1 = go to submit
  const backPhaseAfter3: Phase = needsCoachScreen ? 2 : 1;

  const showNextOnPhase1 = !!coachRating && !!ccRating && (needsCoachScreen || needsCcScreen);

  // ─── State reset ─────────────────────────────────────────────────────────

  const resetState = () => {
    setPhase(1);
    setCoachRating(null);
    setCcRating(null);
    setCoachReasonIds([]);
    setCcReasonIds([]);
    setCoachComment('');
    setCcComment('');
    setIsSubmitted(false);
  };

  // ─── Submission helpers ──────────────────────────────────────────────────

  const buildPayload = (final: boolean) => ({
    student_id: userData?.user_id,
    webinar_id: webinarId,
    rating: coachRating,
    reason_ids: coachReasonIds.map(Number),
    cc_rating: ccRating,
    cc_reason_ids: ccReasonIds.map(Number),
    comment: coachComment.trim(),
    cc_comment: ccComment.trim(),
    is_submitted: final,
    source: 'web' as const,
  });

  const partialSave = () => {
    if (coachRating && ccRating && !isSubmitted) {
      submitFeedbackMutation.mutate(buildPayload(false));
    }
  };

  const finalSubmit = () => {
    if (!coachRating || !ccRating) return;
    submitFeedbackMutation.mutate(buildPayload(true), {
      onSuccess: () => {
        setIsSubmitted(true);
        onSubmit({student_id: userData?.user_id, webinar_id: webinarId});
        onClose();
        resetState();
      },
    });
  };

  // ─── Button press handler ─────────────────────────────────────────────────

  const handlePrimaryPress = () => {
    if (phase === 1) {
      if (showNextOnPhase1) {
        setPhase(nextPhaseAfter1);
        partialSave();
      } else {
        finalSubmit();
      }
    } else if (phase === 2) {
      if (nextPhaseAfter2 === 3) {
        setPhase(3);
        partialSave(); // save coach answers before moving on
      } else {
        finalSubmit();
      }
    } else {
      finalSubmit();
    }
  };

  const handleBack = () => {
    if (phase === 2) setPhase(1);
    else if (phase === 3) setPhase(backPhaseAfter3);
  };

  // ─── Close handler ────────────────────────────────────────────────────────

  const handleClose = () => {
    partialSave();
    onClose();
    // Don't resetState() here — keep data so user can resume
  };

  // ─── Renderers ───────────────────────────────────────────────────────────

  const toggleReason = (type: 'coach' | 'cc', id: string) => {
    const setter = type === 'coach' ? setCoachReasonIds : setCcReasonIds;
    setter(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
  };

  const ratingLabels = ['', 'Poor', 'Fair', 'Good', 'Very Good', 'Excellent'];

  const renderStars = (
    currentRating: number | null,
    onSelect: (v: number) => void,
    hoverRating: number | null,
    setHoverRating: (v: number | null) => void,
  ) => {
    const displayRating = hoverRating !== null ? hoverRating : currentRating;
    return (
      <View style={styles.starsContainer}>
        <View style={styles.starRow}>
          {[1, 2, 3, 4, 5].map(star => {
            const active = displayRating !== null && star <= displayRating;
            return (
              <TouchableOpacity
                key={star}
                activeOpacity={0.7}
                onPress={() => onSelect(star)}
                onMouseEnter={() => setHoverRating(star)}
                onMouseLeave={() => setHoverRating(null)}
                style={styles.starButton}>
                <MaterialIcons
                  name={active ? 'star' : 'star-border'}
                  size={FontSizes['36']}
                  color={active ? '#FFD54F' : CCColors.Grey.Shade200}
                />
              </TouchableOpacity>
            );
          })}
        </View>
        <View style={styles.starLabelsRow}>
          <CCText style={styles.starLabel}>
            {displayRating ? ratingLabels[displayRating] : ''}
          </CCText>
        </View>
      </View>
    );
  };

  const renderChips = (type: 'coach' | 'cc', reasons: any[], selectedIds: string[]) => (
    <View style={styles.chipWrap}>
      {reasons.map(item => {
        const id = String(item?.id);
        const selected = selectedIds.includes(id);
        return (
          <TouchableOpacity
            key={id}
            onPress={() => toggleReason(type, id)}
            activeOpacity={0.85}
            style={[styles.chip, selected && styles.chipActive]}>
            {selected && (
              <MaterialIcons
                name="check"
                size={FontSizes['14']}
                color={CCColors?.Rewards?.TextRed}
                style={{marginRight: 6}}
              />
            )}
            <CCText
              fontSize={FontSizes['13']}
              color={CCColors?.TextColor?.Dark}
              style={selected && styles.chipTextActive}>
              {item?.label}
            </CCText>
          </TouchableOpacity>
        );
      })}
    </View>
  );

  const renderCommentBox = (
    value: string,
    onChange: (t: string) => void,
    placeholder: string,
  ) => (
    <View style={styles.commentBox}>
      <TextInput
        value={value}
        onChangeText={onChange}
        placeholder={placeholder}
        placeholderTextColor={CCColors?.TextColor?.Grey}
        multiline
        maxLength={250}
        style={styles.textInput}
      />
    </View>
  );

  // ─── Phase titles ─────────────────────────────────────────────────────────

  const titleMap: Record<Phase, string> = {
    1: 'Rate your class experience',
    2: 'Help us improve – Coach',
    3: 'Help us improve – CircleChess',
  };

  // ─── Primary button label ─────────────────────────────────────────────────

  const primaryLabel = () => {
    if (phase === 1) return showNextOnPhase1 ? 'Next' : 'Submit';
    if (phase === 2) return nextPhaseAfter2 === 3 ? 'Next' : 'Submit';
    return 'Submit';
  };

  const primaryDisabled =
    !coachRating || !ccRating || submitFeedbackMutation?.isLoading;

  // ─── JSX ─────────────────────────────────────────────────────────────────

  return (
    <SlideModal
      visible={visible}
      onClose={handleClose}
      type="center"
      showHeader={false}
      customStyle={{width: isPhone ? '90%' : 380}}
      customBodyStyle={{paddingTop: 16}}>
      <View style={styles.card}>
        {/* Custom Close Button */}
        <TouchableOpacity 
          style={styles.closeButton} 
          onPress={handleClose}
          activeOpacity={0.7}>
          <MaterialIcons name="close" size={20} color={CCColors.TextColor.Grey} />
        </TouchableOpacity>

        {/* Header */}
        <CCText
          fontSize={FontSizes['20']}
          style={styles.title}
          color={CCColors?.TextColor?.Primary}>
          {titleMap[phase]}
        </CCText>
        <CCText
          fontSize={FontSizes['14']}
          color={CCColors?.TextColor?.Grey}
          style={styles.subtitle}>
          {topic} · {coachName}
        </CCText>

        <ScrollView showsVerticalScrollIndicator={false} style={{marginTop: 52}}>
          {/* ── Phase 1: Dual ratings ── */}
          {phase === 1 && (
            <View>
              <View style={styles.questionGroup}>
                <CCText style={styles.questionText}>How would you rate the coach?</CCText>
                {renderStars(coachRating, setCoachRating, coachHover, setCoachHover)}
              </View>
              
              <Divider></Divider>

              <View style={styles.questionGroup}>
                <CCText style={[styles.questionText, { marginTop: 20 }]}>Overall experience with CircleChess?</CCText>
                {renderStars(ccRating, setCcRating, ccHover, setCcHover)}
              </View>
            </View>
          )}

          {/* ── Phase 2: Coach improvement ── */}
          {phase === 2 && (
            <View>
              <View style={styles.sectionHeader}>
                {/* <CCText style={styles.sectionTag}>Coach</CCText> */}
                <CCText style={styles.sectionTitle}>What can the coach improve?</CCText>
              </View>
              {renderChips('coach', coachReasons, coachReasonIds)}
              {renderCommentBox(
                coachComment,
                setCoachComment,
                'Coach feedback (optional)',
              )}
            </View>
          )}

          {/* ── Phase 3: CircleChess improvement ── */}
          {phase === 3 && (
            <View>
              <View style={styles.sectionHeader}>
                {/* <CCText style={styles.sectionTag}>CircleChess</CCText> */}
                <CCText style={styles.sectionTitle}>What can CircleChess improve?</CCText>
              </View>
              {renderChips('cc', ccReasons, ccReasonIds)}
              {renderCommentBox(
                ccComment,
                setCcComment,
                'CircleChess feedback (optional)',
              )}
            </View>
          )}
        </ScrollView>

        {/* Buttons */}
        <View style={styles.buttonWrap}>
          {phase > 1 && (
            <TouchableOpacity style={styles.backBtn} onPress={handleBack}>
              <CCText color={CCColors.TextColor.Grey}>← Back</CCText>
            </TouchableOpacity>
          )}
          <CCButton
            disabled={primaryDisabled}
            style={styles.submitBtn}
            onPress={handlePrimaryPress}
            titleStyle={{fontSize: FontSizes[16]}}>
            {primaryLabel()}
          </CCButton>
        </View>
      </View>
    </SlideModal>
  );
}

const styles = StyleSheet.create({
  card: {
    flex: 1,
    backgroundColor: CCColors?.White,
    paddingHorizontal: isPhone ? 0 : 20,
    borderRadius: 10,
  },
  title: {
    fontWeight: '700',
    textAlign: 'center',
  },
  subtitle: {
    textAlign: 'center',
    marginTop: 4,
  },
  questionGroup: {
    marginBottom: 24,
  },
  questionText: {
    fontSize: FontSizes['15'],
    fontWeight: '600',
    textAlign: 'center',
    marginBottom: 4,
    color: CCColors?.TextColor?.Primary,
  },
  starRow: {
    flexDirection: 'row',
    justifyContent: 'center',
  },
  starsContainer: {
    alignItems: 'center',
    marginTop: 8,
  },
  starLabelsRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    width: '100%',
    marginTop: 6,
    height: 24, // Fixed height to prevent layout bounce
  },
  starLabel: {
    fontSize: FontSizes['13'],
    fontWeight: '600',
    color: CCColors?.TextColor?.Grey,
    textAlign: 'center',
  },
  starButton: {
    paddingHorizontal: 2,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 14,
  },
  sectionTag: {
    fontSize: FontSizes['11'],
    fontWeight: '700',
    color: CCColors?.Rewards?.TextRed,
    textTransform: 'uppercase',
    letterSpacing: 0.8,
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 4,
    backgroundColor: updateColorWithOpacity(CCColors?.Rewards?.TextRed, 0.1),
    overflow: 'hidden',
  },
  sectionTitle: {
    fontSize: FontSizes['15'],
    fontWeight: '600',
    color: CCColors?.TextColor?.Primary,
    flex: 1,
  },
  chipWrap: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: CCColors?.OnboardingGrey,
    backgroundColor: CCColors?.Grey?.BG100,
  },
  chipActive: {
    backgroundColor: updateColorWithOpacity(CCColors?.Rewards?.TextRed, 0.2),
    borderColor: CCColors?.Rewards?.TextRed,
  },
  chipTextActive: {
    color: CCColors?.Rewards?.TextRed,
    fontWeight: '600',
  },
  commentBox: {
    marginTop: 16,
  },
  textInput: {
    minHeight: 80,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: CCColors?.OnboardingGrey,
    padding: 12,
    fontSize: FontSizes['14'],
    color: CCColors.TextColor.Dark,
    textAlignVertical: 'top',
    backgroundColor: CCColors?.White,
  },
  buttonWrap: {
    marginTop: 24,
    width: '100%',
    alignItems: 'center',
  },
  submitBtn: {
    width: '70%',
    minWidth: 200,
  },
  divider: {
    height: 1,
    backgroundColor: CCColors?.Grey?.Shade100,
    marginVertical: 12,
    width: '90%',
    alignSelf: 'center',
  },
  closeButton: {
    position: 'absolute',
    top: -4,
    right: 0,
    padding: 8,
    zIndex: 10,
  },
  backBtn: {
    marginBottom: 10,
    padding: 8,
    alignSelf: 'flex-start',
  },
});
